import { useRef, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CampaignIcon from '@mui/icons-material/Campaign';
import ChatIcon from '@mui/icons-material/Chat';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { formatPhone } from '@functions/format-phone';
import { leadChannel, leadChannelColor } from '@layouts/pages/CRM/lead/lead-channel';
import { UserResponse } from 'layouts/pages/System/users/services/responses/user.response';
import { LeadStatusResponse } from '@layouts/pages/Settings/general/lead-status/services/responses/lead-status.response';
import { DRAG_DISTANCE } from '../../hooks/use-crm-drag.hook';
import { LeadResponse } from '../../services/responses/lead.response';
import { getCrmColors } from '../../crm-theme';
import { formatCrmDateTime, formatCrmShortDateTime } from '../../formatters/crm-datetime';
import { formatRoundedBrl, leadAmount } from '../../formatters/crm-lead-amount';
import { SOURCE_DETAIL_KEY } from '../../formatters/lead-source-label';
import { matchesScheduleFilter } from '../../filters/crm-schedule-filter';
import { LeadAssignMenu, LeadStatusMenu } from '../shared/crm-lead-menus';
import { useProfilePermissions } from '@hooks/use-profile-permissions.hook';
import { assignableUsers } from '../../filters/crm-assignable-users';
import { CrmLeadStatusChip } from '../shared/crm-lead-status-chip';
import { CrmLeadOwnerAvatar } from '../shared/crm-lead-owner-avatar';
import {
    amountSx, cardSx, channelIconAreaSx, hiddenTagsSx, nextScheduleSx, tagStripSx,
} from './crm-lead-card.styles';

/// Tags mostradas no card antes do "+N".
const CARD_TAG_LIMIT = 5;

interface Props {
    lead: LeadResponse;
    onOpen: (lead: LeadResponse) => void;
    statuses?: LeadStatusResponse[];
    onChangeStatus?: (lead: LeadResponse, statusId: string) => void;
    onOpenSchedule?: (lead: LeadResponse) => void;
    boardActive: boolean;
    users?: UserResponse[];
    onAssign?: (lead: LeadResponse, userId: string | null) => void;
    /// Vem do Multicanal: destaca o card em vez de abrir o modal sobre o quadro.
    focused?: boolean;
}

export const CrmLeadCard = ({
    lead, onOpen, statuses, onChangeStatus, onOpenSchedule, boardActive, users, onAssign, focused = false,
}: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
    const [assignAnchor, setAssignAnchor] = useState<HTMLElement | null>(null);
    const pressedAt = useRef<{ x: number; y: number } | null>(null);
    const navigate = useNavigate();

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: lead.id,
        data: { type: 'lead', lead },
        disabled: !boardActive,
    });

    const canChangeStatus = Boolean(onChangeStatus) && boardActive && (statuses?.length ?? 0) > 0;
    const { canAssignLeads, userId } = useProfilePermissions();
    const canAssign = Boolean(onAssign) && boardActive
        && assignableUsers(users ?? [], lead.assignedUserId, canAssignLeads, userId).length > 0;
    const amount = leadAmount(lead);
    const hiddenTags = lead.tags?.slice(CARD_TAG_LIMIT) ?? [];

    // O lead só tem conversa se nasceu de um canal; sem isso o texto da última mensagem é só texto.
    const hasConversation = Boolean(lead.chatwootConversationId);
    const channelColor = leadChannelColor(lead.source, cc);

    const openConversation = (e: React.SyntheticEvent) => {
        if (!hasConversation) return;

        e.stopPropagation();
        navigate(`/multicanal?conversa=${lead.chatwootConversationId}`);
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        // Clique em menu do card chega pelo portal do React sem estar no card no DOM.
        if (!e.currentTarget.contains(e.target as Node)) return;

        // Soltar o card no mesmo lugar também gera clique; abaixo da distância do arrasto, é clique.
        const start = pressedAt.current;
        if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) >= DRAG_DISTANCE) return;

        onOpen(lead);
    };

    return (
        <Box
            ref={setNodeRef}
            id={`lead-card-${lead.id}`}
            style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
            {...attributes}
            {...listeners}
            onPointerDownCapture={(e) => { pressedAt.current = { x: e.clientX, y: e.clientY }; }}
            onClick={handleClick}
            sx={cardSx(cc, lead.statusColor, focused)}
        >
            {/* Faixas finas: chips em linha cresciam o card; a lista completa fica no lead. */}
            {lead.tags?.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, mb: 0.25 }}>
                    {lead.tags.slice(0, CARD_TAG_LIMIT).map((tag) => (
                        <Tooltip key={tag.id} title={tag.name}>
                            <Box aria-label={tag.name} sx={tagStripSx(tag.color)} />
                        </Tooltip>
                    ))}
                    {hiddenTags.length > 0 && (
                        <Tooltip title={hiddenTags.map((tag) => tag.name).join(', ')}>
                            <Typography variant="caption" sx={hiddenTagsSx(cc)}>+{hiddenTags.length}</Typography>
                        </Tooltip>
                    )}
                </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
                <CrmLeadStatusChip lead={lead} size="small" onOpenMenu={canChangeStatus ? setStatusAnchor : undefined} />

                <Tooltip title={`${t('crmKanban.criadoEm')} ${formatCrmDateTime(lead.createdAt)}`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <CalendarTodayIcon sx={{ fontSize: 11, color: cc.textSecondary }} />
                        <Typography variant="caption" sx={{ color: cc.textSecondary }}>
                            {formatCrmShortDateTime(lead.createdAt)}
                        </Typography>
                    </Box>
                </Tooltip>
            </Box>

            {/* Valor à direita do nome: decide a ordem de atendimento e no rodapé competiria. */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography
                    variant="body2"
                    fontWeight={700}
                    noWrap
                    sx={{ lineHeight: 1.3, color: cc.textPrimary, flex: 1, minWidth: 0 }}
                >
                    {lead.contactName}
                </Typography>
                {amount != null && (
                    <Typography variant="caption" sx={amountSx(cc, lead.closedValue != null)}>
                        {formatRoundedBrl(amount)}
                    </Typography>
                )}
            </Box>

            {lead.procedureName && (
                <Typography variant="caption" noWrap sx={{ color: cc.textSecondary, lineHeight: 1.3 }}>
                    {lead.procedureName}
                </Typography>
            )}

            {lead.contactPhone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhoneOutlinedIcon sx={{ fontSize: 11, color: cc.textSecondary }} />
                    <Typography variant="caption" sx={{ color: cc.textSecondary }}>
                        {formatPhone(lead.contactPhone)}
                    </Typography>
                </Box>
            )}

            {/* A descrição fica na tela do lead: no card crescia sem limite. */}
            {/* Só o ícone abre a conversa: com a linha toda, arrastar o card caía nela. */}
            {lead.lastMessage && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={hasConversation ? t('crmKanban.abrirConversa') : ''}>
                        <Box
                            role={hasConversation ? 'button' : undefined}
                            tabIndex={hasConversation ? 0 : undefined}
                            aria-label={hasConversation ? t('crmKanban.abrirConversa') : undefined}
                            onClick={openConversation}
                            onKeyDown={(e) => e.key === 'Enter' && openConversation(e)}
                            // O arrasto começa no pointerdown: parar aqui evita o clique no ícone virar arrasto.
                            onPointerDown={(e) => hasConversation && e.stopPropagation()}
                            sx={channelIconAreaSx(channelColor, hasConversation)}
                        >
                            <Box
                                component={leadChannel(lead.source)?.Icon ?? ChatIcon}
                                sx={{ fontSize: 12, color: channelColor }}
                            />
                        </Box>
                    </Tooltip>
                    <Typography variant="caption" noWrap sx={{ lineHeight: 1.3, color: cc.textSecondary, minWidth: 0 }}>
                        {lead.lastMessage}
                    </Typography>
                </Box>
            )}

            {/* Rodapé numa linha só: o compromisso, quando há, ocupa o lugar da última alteração. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {lead.nextScheduledAt ? (
                    <Tooltip title={t('crmKanban.verAtividadeAgendada')}>
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpenSchedule?.(lead);
                            }}
                            onKeyDown={(e) => {
                                if (e.key !== 'Enter' && e.key !== ' ') return;
                                e.preventDefault();
                                onOpenSchedule?.(lead);
                            }}
                            sx={nextScheduleSx(cc, matchesScheduleFilter(lead.nextScheduledAt, 'late'), Boolean(onOpenSchedule))}
                        >
                            <EventAvailableIcon sx={{ fontSize: 11 }} />
                            <Typography variant="caption" fontWeight={700} sx={{ color: 'inherit' }}>
                                {formatCrmShortDateTime(lead.nextScheduledAt)}
                            </Typography>
                        </Box>
                    </Tooltip>
                ) : (
                    <Tooltip title={`${t('crmKanban.ultimaAlteracao')} ${formatCrmDateTime(lead.updatedAt)}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTimeIcon sx={{ fontSize: 11, color: cc.textSecondary }} />
                            <Typography variant="caption" sx={{ color: cc.textSecondary }}>
                                {formatCrmShortDateTime(lead.updatedAt)}
                            </Typography>
                        </Box>
                    </Tooltip>
                )}

                {/* Veio de anúncio: por onde fica à vista (Facebook, Direct...); a campanha, no aviso. */}
                {(lead.sourceCampaign || lead.sourceDetail) && (
                    <Tooltip
                        title={[lead.sourceCampaign, lead.sourceAdId && `ID ${lead.sourceAdId}`].filter(Boolean).join(' · ')}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0 }}>
                            <CampaignIcon sx={{ fontSize: 13, color: cc.eventWarning, flexShrink: 0 }} />
                            {lead.sourceDetail && (
                                <Typography variant="caption" noWrap sx={{ color: cc.eventWarning, fontSize: '0.68rem' }}>
                                    {t(SOURCE_DETAIL_KEY[lead.sourceDetail])}
                                </Typography>
                            )}
                        </Box>
                    </Tooltip>
                )}

                <Tooltip
                    title={lead.assignedUserName
                        ? t('crmKanban.responsavelPor', { nome: lead.assignedUserName })
                        : t('crmKanban.adicionarResponsavel')}
                >
                    <Box sx={{ display: 'flex', ml: 'auto' }}>
                        <CrmLeadOwnerAvatar
                            lead={lead}
                            size="small"
                            onClick={canAssign
                                ? (e) => { e.stopPropagation(); setAssignAnchor(e.currentTarget); }
                                : undefined}
                        />
                    </Box>
                </Tooltip>
            </Box>

            {canAssign && (
                <LeadAssignMenu
                    lead={lead}
                    users={users}
                    anchorEl={assignAnchor}
                    onClose={() => setAssignAnchor(null)}
                    onSelect={(userId) => onAssign(lead, userId)}
                />
            )}

            {canChangeStatus && (
                <LeadStatusMenu
                    lead={lead}
                    statuses={statuses}
                    anchorEl={statusAnchor}
                    onClose={() => setStatusAnchor(null)}
                    onSelect={(statusId) => onChangeStatus(lead, statusId)}
                />
            )}
        </Box>
    );
};
